import { useCallback, useEffect, useRef, useState } from 'react';
import { pausedMsSince } from '../talk/talkOpen';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { REWARD_ICONS, useSettings, type RewardIcon } from '../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from './speak';
import { playCorrectPing } from './ping';
import { endSession, fetchRewardSummary, type RewardSummary } from '../rewards/rewards';

type RoundView = {
  roundId: string;
  programId: string;
  kind: string;
  promptType: 'AUDIO' | 'TEXT';
  promptText: string;
  /** What counts as right, for the adult scoring. Never shown to the learner. */
  hint?: string | null;
  probe?: boolean;
  breakOffered?: boolean;
};

type AttemptResult = {
  correct: boolean;
  roundComplete: boolean;
  attempts: number;
  rewarded: number;
};

type Props = {
  programId: string;
  learnerId?: string;
};

const FEEDBACK_HOLD_MS = 700;

export default function SupervisorScored({ programId, learnerId }: Props) {
  const { token } = useAuth();
  const { settings } = useSettings();
  const [round, setRound] = useState<RoundView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [marked, setMarked] = useState<'CORRECT' | 'INCORRECT' | null>(null);
  const [onBreak, setOnBreak] = useState(false);
  const [collected, setCollected] = useState<RewardSummary | null>(null);
  const [earnedNow, setEarnedNow] = useState(0);
  const [right, setRight] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [fadingOut, setFadingOut] = useState(false);
  const shownAtRef = useRef<number>(0);
  const promptsRef = useRef<number>(0);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const answeredRef = useRef(false);
  const playPromptRef = useRef<() => void>(() => {});

  const startRound = useCallback(async () => {
    setError(null);
    setBusy(true);
    setMarked(null);
    setOnBreak(false);
    stopSpeaking();
    try {
      const r = await api<RoundView>('/games/start', {
        method: 'POST',
        token,
        body: { programId, learnerId },
      });
      setRound(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start round');
    } finally {
      setBusy(false);
      setFadingOut(false);
    }
  }, [programId, learnerId, token]);

  useEffect(() => { startRound(); }, [startRound]);

  useEffect(() => {
    if (!settings.features.rewardTotal) return;
    let cancelled = false;
    void fetchRewardSummary(learnerId ?? null).then((s) => {
      if (!cancelled) setCollected(s);
    });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.rewardTotal]);

  /* SPOKEN ONLY WHEN THE PROGRAM SAYS SO, BUT ALWAYS WRITTEN. The adult has to
     read the prompt to give it, whichever way the learner receives it. */
  const playPrompt = useCallback(() => {
    if (!round) return;
    promptsRef.current += 1;
    if (round.promptType === 'AUDIO' && canSpeak()) {
      speak(round.promptText, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }
  }, [round, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => { playPromptRef.current = playPrompt; }, [playPrompt]);

  useEffect(() => {
    if (!round) return;
    shownAtRef.current = performance.now();
    promptsRef.current = 0;
    const t = window.setTimeout(() => playPromptRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [round]);

  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    if (!marked) return;
    const transitionMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
    const fadeStart = window.setTimeout(() => setFadingOut(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => { startRound(); }, FEEDBACK_HOLD_MS + transitionMs);
    return () => {
      window.clearTimeout(fadeStart);
      window.clearTimeout(advance);
    };
  }, [marked, startRound, settings.roundTransitionMs, settings.roundTransitionType]);

  useEffect(() => {
    const session = sessionIdRef.current;
    return () => {
      void endSession(programId, session, answeredRef.current);
    };
  }, [programId]);

  async function score(ok: boolean) {
    if (!round || marked) return;
    stopSpeaking();
    try {
      const result = await api<AttemptResult>(`/games/rounds/${round.roundId}/attempt`, {
        method: 'POST',
        token,
        body: {
          tile: ok ? 'CORRECT' : 'INCORRECT',
          respondedMs: Math.round(performance.now() - shownAtRef.current - pausedMsSince(shownAtRef.current)),
          promptsPlayed: promptsRef.current,
        },
      });
      answeredRef.current = true;
      if (result.rewarded > 0) setEarnedNow((n) => n + result.rewarded);
      if (result.correct) {
        setRight((n) => n + 1);
        if (settings.correctPingEnabled) playCorrectPing();
      } else {
        setWrong((n) => n + 1);
      }
      setMarked(ok ? 'CORRECT' : 'INCORRECT');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record that mark');
    }
  }

  async function takeBreak() {
    if (!round) return;
    stopSpeaking();
    await api<{ recorded: boolean }>(`/games/rounds/${round.roundId}/break`, {
      method: 'POST',
      token,
    }).catch(() => undefined);
    setOnBreak(true);
  }

  if (error) {
    return (
      <div className="card">
        <div className="error" role="alert">{error}</div>
      </div>
    );
  }
  if (busy && !round) return null;
  if (!round) return null;

  if (onBreak) {
    return (
      <div className="card">
        <h2>Break time</h2>
        <p className="field-hint">
          Asking to stop is written down as asking to stop. It is not a wrong answer and it
          does not count against anything.
        </p>
        <div className="toolbar">
          <button className="btn" onClick={() => { void startRound(); }}>
            Ready to carry on
          </button>
        </div>
      </div>
    );
  }

  const effectiveMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
  const transitionStyle = { '--round-transition-ms': `${effectiveMs}ms` } as React.CSSProperties;
  const typeClass = `round-${settings.roundTransitionType.toLowerCase()}`;

  return (
    <>
      {settings.features.rewardTotal && collected?.enabled && (
        <div className="board-total">
          <span aria-hidden="true">
            {REWARD_ICONS[collected.icon as RewardIcon] ?? REWARD_ICONS.star}
          </span>
          <strong>{collected.total + earnedNow}</strong>
          <span>{collected.name}</span>
        </div>
      )}
      {round.probe && (
        <div className="banner" role="status">
          First go today, so no help is given and nothing is earned. It shows what they can
          do on their own.
        </div>
      )}

      <div
        className={`card ${typeClass}${fadingOut ? ' fading-out' : ''}`}
        style={transitionStyle}
      >
        <div className="muted" style={{ marginBottom: 6 }}>
          ✓ {right}{' · '}✗ {wrong}
        </div>

        <div className="concept-prompt-big">{round.promptText}</div>
        {round.hint && (
          <div className="muted" style={{ textAlign: 'center', marginBottom: 12 }}>
            {round.hint}
          </div>
        )}

        <div className="toolbar" style={{ justifyContent: 'center' }}>
          <button
            className="btn"
            onClick={() => void score(true)}
            disabled={!!marked}
            aria-label="Mark correct"
          >
            ✓ Correct
          </button>
          <button
            className="btn secondary"
            onClick={() => void score(false)}
            disabled={!!marked}
            aria-label="Mark incorrect"
          >
            ✗ Incorrect
          </button>
          <button
            className="btn ghost"
            onClick={() => playPromptRef.current()}
            disabled={!!marked}
            aria-label="Give the prompt again"
          >
            Say it again
          </button>
        </div>
      </div>

      {round.breakOffered && !marked && (
        <div className="toolbar break-bar">
          <button className="btn secondary" onClick={() => void takeBreak()}>
            I need a break
          </button>
        </div>
      )}
    </>
  );
}
